import { addActingOrganizationHeader } from '@koob/utils';
import { API_NODE_BASE } from '../constants';
import { get, put } from './index';

// KD-3848 — the AI assistant on hotel and experience pages.
const PRODUCT_TYPES = ['hotel', 'experience'];

// Ids come from the page URL: encoded, so a "/" or ".." in one cannot point
// the authenticated request at another route.
const productPath = (productType, productId) => {
  if (!PRODUCT_TYPES.includes(productType)) {
    throw new Error(`Unknown product type: ${productType}`);
  }
  return `/api/internal/assistant/${productType}s/${encodeURIComponent(
    productId,
  )}`;
};

export const getAssistantQuestions = ({ productType, productId }) =>
  get(`${productPath(productType, productId)}/questions`);

export const sendAssistantFeedback = (exchangeId, feedback, reason) =>
  put(
    `/api/internal/assistant/exchanges/${encodeURIComponent(
      exchangeId,
    )}/feedback`,
    { feedback, ...(reason ? { reason } : {}) },
  );

export class AssistantError extends Error {
  constructor(status, code) {
    super(code ?? `HTTP ${status}`);
    this.status = status;
    this.code = code;
  }
}

/**
 * Asks a question; yields the server-sent events as they arrive: { type:
 * 'text', text } (and { type: 'not_provided' } once the page turns out not to
 * hold the answer), then { type: 'sources', sources } and { type: 'done',
 * exchangeId, notProvided }, or { type: 'error' }. axios cannot stream in the browser, so this goes through fetch
 * with the headers the axios client sets.
 */
export async function* askAssistant({ productType, productId, body, signal }) {
  const token =
    localStorage.getItem('impersonationToken') ||
    localStorage.getItem('accessToken');
  const { headers } = addActingOrganizationHeader({
    headers: {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const response = await fetch(
    `${API_NODE_BASE}${productPath(productType, productId)}/messages`,
    { method: 'POST', headers, body: JSON.stringify(body), signal },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new AssistantError(response.status, error?.code);
  }

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += value;
    let end;
    while ((end = buffer.indexOf('\n\n')) >= 0) {
      const frame = buffer.slice(0, end);
      buffer = buffer.slice(end + 2);
      const type = /^event: (.*)$/m.exec(frame)?.[1];
      const data = /^data: (.*)$/m.exec(frame)?.[1];
      if (type && data) yield { type, ...JSON.parse(data) };
    }
  }
}
